import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  InputLabel,
  Chip,
  Grid,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Card,
  CardContent,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Collapse,
  Checkbox,
  FormGroup,
  Alert
} from "@mui/material";
import CircleIcon from "@mui/icons-material/FiberManualRecord";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PushPinIcon from "@mui/icons-material/PushPin";
import NoteIcon from "@mui/icons-material/Note";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArchiveIcon from "@mui/icons-material/Archive";
import FormLogHistory from "./FormLogHistory";
import YearNavigation from "./YearNavigation";
import { IndividualSkeleton } from "./SkeletonLoaders";
import DirectRequestButton from "./DirectRequestButton";

// Helper function to safely parse date strings
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  try {
    // Split the date string into components
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    
    // Create a new date object using local time zone
    // Month is 0-indexed in JavaScript Date
    const date = new Date(year, month - 1, day, 12, 0, 0);
    
    // Verify that the date is valid
    if (isNaN(date.getTime())) {
      console.error(`Invalid date string: ${dateString}`);
      return null;
    }
    
    console.log(`Parsed date '${dateString}' to:`, date);
    return date;
  } catch (err) {
    console.error(`Error parsing date '${dateString}':`, err);
    return null;
  }
};

// Helper function to format dates as MM-DD-YYYY
const formatDateDisplay = (dateString) => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${month}-${day}-${year}`;
};

const People = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBuilding, setIsAuthenticated } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get year from query params
  const queryParams = new URLSearchParams(location.search);
  const yearFromQuery = queryParams.get('year');

  const fetchAuth = async (url, options = {}) => {
    try {
      const response = await fetchWithAuth(url, options);
      
      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (error?.message?.toLowerCase().includes('session expired')) {
        setIsAuthenticated(false);
        navigate('/login?session=expired');
      }
      console.error('API request failed:', error);
      throw error;
    }
  };


  const [individual, setIndividual] = useState(null);
  const [filteredIndividuals, setFilteredIndividuals] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [reportData, setReportData] = useState([]); // Source of truth for reports
  const [displayedReports, setDisplayedReports] = useState([]); // Filtered and modified reports
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [updatedReports, setUpdatedReports] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null); // For update feedback
  const [currentYear, setCurrentYear] = useState(null); // For yearly navigation - will be set by YearNavigation
  const [showLogHistory, setShowLogHistory] = useState(false); // Toggle log history view
  const [editingIndividual, setEditingIndividual] = useState(false);
  const [editedIndividual, setEditedIndividual] = useState({});
  const [editingContact, setEditingContact] = useState(null);
  const [editedContact, setEditedContact] = useState({});
  const [addingNewContact, setAddingNewContact] = useState(false);
  const [newContact, setNewContact] = useState({
    contact_name: '',
    relationship: '',
    email: '',
    phone_number: ''
  });

  // Individual notes state
  const [individualNotes, setIndividualNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newNoteData, setNewNoteData] = useState({ title: '', content: '' });
  const [editedNoteData, setEditedNoteData] = useState({ title: '', content: '' });
  const [notesExpanded, setNotesExpanded] = useState(false);

  // Manage Forms Dialog state
  const [manageFormsOpen, setManageFormsOpen] = useState(false);
  const [availableReportTypes, setAvailableReportTypes] = useState([]);
  const [selectedFormIds, setSelectedFormIds] = useState([]);
  const [manageFormsLoading, setManageFormsLoading] = useState(false);
  const [drOrdersDateOfIssue, setDrOrdersDateOfIssue] = useState('');
  const DR_ORDERS_FORM_ID = 15;

  // Track initial state for reports
  const [initialReportData, setInitialReportData] = useState({});

  const calculateDaysBetween = (date1, date2) => {
    if (!date1 || !date2) return "-";
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    if (isNaN(d1) || isNaN(d2)) return "-";
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.round(Math.abs((d1 - d2) / oneDay));
  };

  const fetchIndividualBasicData = async (individualId) => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch individual data with error handling
      const individualData = await fetchAuth(`/api/individuals/${individualId}`);
      setIndividual(individualData);

      // Fetch contacts with error handling
      const contactsData = await fetchAuth(`/api/contacts?individual_id=${individualId}`);
      setContacts(Array.isArray(contactsData) ? contactsData : []);

      // Fetch individual notes
      fetchIndividualNotes(individualId);

    } catch (error) {
      console.error("Error fetching individual data:", error);
      setError(error.message || "Failed to load individual data");

      // Check if this is an auth error
      if (error.message.includes("session has expired")) {
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFormsData = async (individualId, year) => {
    if (!year) {
      setReportData([]);
      setInitialReportData({});
      setDisplayedReports([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch forms by year with error handling
      const formsData = await fetchAuth(`/api/individuals/${individualId}/forms/${year}`);
      
      // Ensure formsData is always an array
      const formsArray = Array.isArray(formsData) ? formsData : [];
      setReportData(formsArray);
      
      // Save initial data for change detection
      setInitialReportData(
        formsArray.reduce((acc, report) => {
          acc[report.id] = { ...report };
          return acc;
        }, {})
      );
      
      // Apply filter with the reports array
      applyFilterToDisplayedReports(formsArray);
    } catch (error) {
      console.error("Error fetching forms data:", error);
      setError(error.message || "Failed to load forms data");
      
      // Check if this is an auth error
      if (error.message.includes("session has expired")) {
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Apply filter whenever reportData, filter, or searchTerm changes
    applyFilterToDisplayedReports(reportData);
  }, [reportData, filter, searchTerm]);

  // Effect for fetching individuals list
  useEffect(() => {
    const fetchIndividuals = async () => {
      try {
        const data = await fetchAuth("/api/individuals");
        setIndividuals(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching individuals:", error);
        setError(error.message || "Failed to load individuals");
      }
    };

    fetchIndividuals();
  }, []);

  // Effect for when individual ID changes - fetch basic data and reset year
  useEffect(() => {
    if (id) {
      // Don't reset year to null - let YearNavigation handle year selection
      fetchIndividualBasicData(id); // Fetch individual and contacts immediately
    }
  }, [id]);

  // Effect to set year from query parameter
  useEffect(() => {
    if (yearFromQuery && !currentYear) {
      setCurrentYear(parseInt(yearFromQuery));
    }
  }, [yearFromQuery]);

  // Effect for when current year changes - fetch forms data
  useEffect(() => {
    if (id && currentYear) {
      fetchFormsData(id, currentYear);
    }
  }, [currentYear]);

  useEffect(() => {
    if (selectedBuilding && Array.isArray(individuals)) {
      setFilteredIndividuals(
        selectedBuilding.name === "All"
          ? individuals
          : individuals.filter(
              (individual) =>
                individual.building_location_id === selectedBuilding.id
            )
      );
    } else {
      setFilteredIndividuals([]);
    }
  }, [selectedBuilding, individuals]);

  const applyFilterToDisplayedReports = (sourceData) => {
    // Ensure sourceData is an array
    if (!Array.isArray(sourceData)) {
      console.warn("Source data is not an array in applyFilterToDisplayedReports", sourceData);
      setDisplayedReports([]);
      return;
    }
    
    // Apply selected filter (all, collected, awaiting)
    let filtered = [...sourceData]; // Create a copy to avoid mutating the original
    
    if (filter === "collected") {
      filtered = filtered.filter((report) => report.collected === 1);
    } else if (filter === "awaiting") {
      filtered = filtered.filter((report) => report.collected === 0);
    }

    // Apply search filter - ensure report properties exist before accessing
    let searchFiltered = filtered;
    
    if (searchTerm && searchTerm.trim() !== "") {
      searchFiltered = filtered.filter(report => 
        (report.report_name && report.report_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Add unsaved changes from updatedReports
    const withUnsavedChanges = searchFiltered.map((report) => ({
      ...report,
      ...(updatedReports[report.id] || {}),
    }));

    setDisplayedReports(withUnsavedChanges);
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const handleIndividualSelect = (event) => {
    const individualId = event.target.value;
    navigate(`/people/${individualId}`);
  };

  const handleCollectedChange = (reportId, newValue) => {
    const collectedDate = newValue === 1 ? new Date().toISOString().slice(0, 10) : null;
    
    setDisplayedReports((prevReports) =>
      prevReports.map((report) =>
        report.id === reportId
          ? {
              ...report,
              collected: newValue,
              collected_date: collectedDate,
            }
          : report
      )
    );

    // Track changes to be saved
    setUpdatedReports((prev) => ({
      ...prev,
      [reportId]: {
        ...prev[reportId],
        collected: newValue,
        collected_date: collectedDate,
      },
    }));
  };
  
  const handleDateChange = (date, reportId, dateType) => {
    // Format the date as YYYY-MM-DD or null if no date provided
    let newDateValue = null;
    
    if (date) {
      // Format the date as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      newDateValue = `${year}-${month}-${day}`;
      
      console.log(`Selected date: ${date}`);
      console.log(`Formatted as: ${newDateValue}`);
    }
  
    // Update displayed reports
    setDisplayedReports((prevReports) =>
      prevReports.map((report) =>
        report.id === reportId
          ? {
              ...report,
              [dateType]: newDateValue,
              collected: dateType === "collected_date" ? (newDateValue ? 1 : 0) : report.collected,
            }
          : report
      )
    );
  
    // Track changes to be saved
    setUpdatedReports((prev) => ({
      ...prev,
      [reportId]: {
        ...(prev[reportId] || {}),
        [dateType]: newDateValue,
        collected: dateType === "collected_date" ? (newDateValue ? 1 : 0) : prev[reportId]?.collected,
      },
    }));
  };
  
  const handleUpdateAll = async () => {
    if (Object.keys(updatedReports).length === 0) return;
  
    // Only show loading indicator for the button, not the whole page
    setUpdateStatus('loading');
    setError(null);
    
    try {
      const updateCount = Object.keys(updatedReports).length;
      
      const updatePromises = Object.entries(updatedReports).map(([reportId, changes]) => {
        const originalReport = initialReportData[reportId];
        if (!originalReport) return Promise.resolve(); // Skip if no original data
        
        const dataToSend = {};
      
        dataToSend.collected =
          changes.collected !== undefined ? changes.collected : originalReport.collected;
          
        // Ensure dates are in proper YYYY-MM-DD format
        dataToSend.due_date = 
          changes.due_date !== undefined ? changes.due_date : originalReport.due_date;
        // Make sure we're not sending any ISO string with time parts
        if (dataToSend.due_date && dataToSend.due_date.includes('T')) {
          dataToSend.due_date = dataToSend.due_date.split('T')[0];
        }
        
        dataToSend.collected_date =
          changes.collected_date !== undefined ? changes.collected_date : originalReport.collected_date;
        // Make sure we're not sending any ISO string with time parts
        if (dataToSend.collected_date && dataToSend.collected_date.includes('T')) {
          dataToSend.collected_date = dataToSend.collected_date.split('T')[0];
        }
      
        if (
          dataToSend.collected === originalReport.collected &&
          dataToSend.due_date === originalReport.due_date &&
          dataToSend.collected_date === originalReport.collected_date
        ) {
          return Promise.resolve(); // Skip if no changes
        }
      
        return fetchAuth(`/api/reports/${reportId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(dataToSend),
        });
      });
    
      await Promise.all(updatePromises);
      
      // Since the API doesn't return updated data, we'll use our local updates
      // Create new reports array with the updates applied
      const newReports = reportData.map(report => {
        if (updatedReports[report.id]) {
          // Merge the original report with the updates
          return {
            ...report,
            ...updatedReports[report.id]
          };
        }
        return report;
      });
      
      setReportData(newReports);
      
      // Update initial data for change detection
      const updatedInitialData = {...initialReportData};
      Object.entries(updatedReports).forEach(([reportId, updates]) => {
        const originalReport = initialReportData[reportId];
        if (originalReport) {
          updatedInitialData[reportId] = {
            ...originalReport,
            ...updates
          };
        }
      });
      setInitialReportData(updatedInitialData);
      
      // Apply filter immediately to update displayed reports with the new data
      applyFilterToDisplayedReports(newReports);
      
      // Clear pending changes after UI update
      setUpdatedReports({});
      
      // Set success status with the count of updated reports
      setUpdateStatus('success');
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setUpdateStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error("Error updating data:", error);
      setError("Failed to update reports. Please try again.");
      setUpdateStatus('error');
      
      // Auto-clear error after 5 seconds
      setTimeout(() => {
        setUpdateStatus(null);
      }, 5000);
    }
  };

  const handleViewNotes = (reportId) => {
    setSelectedNotes(["Sample note for the report"]);
    setNotesModalVisible(true);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setSelectedNotes([...selectedNotes, newNote]);
      setNewNote("");
    }
  };

  const getStatusColor = (report) => {
    if (!report.due_date) return 'lightgreen';

    // If already collected, show green
    if (report.collected) return 'lightgreen';

    const dueDate = new Date(report.due_date);
    const today = new Date();
    const diffTime = today - dueDate;
    const daysDiff = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // If daysDiff is positive, the form is overdue
    if (daysDiff > 90) return '#F55F5F'; // Red - More than 90 days overdue
    if (daysDiff > 60) return '#E0E000'; // Yellow - 61-90 days overdue
    if (daysDiff > 45) return 'lightblue'; // Blue - 46-60 days overdue
    if (daysDiff > 0) return 'lightgreen'; // Green - 1-45 days overdue

    // If daysDiff is negative, the form is not yet due
    // Check if it's upcoming (due within 30 days)
    if (daysDiff >= -30) return '#FFA726'; // Orange - Due within 30 days (upcoming)

    return 'lightgreen'; // Green - Not due for more than 30 days
  };

  // Helper to check if a form is upcoming (due within 30 days, not collected)
  const isUpcoming = (report) => {
    if (!report.due_date || report.collected) return false;
    const dueDate = new Date(report.due_date);
    const today = new Date();
    const diffTime = today - dueDate;
    const daysDiff = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return daysDiff >= -30 && daysDiff <= 0;
  };

  // Get status label and color for chip display
  const getStatusChip = (report) => {
    if (report.collected) {
      return { label: 'Form Collected', color: 'success' };
    }
    if (isUpcoming(report)) {
      return { label: 'Due Soon', color: 'warning' };
    }
    return { label: 'Awaiting Form', color: 'error' };
  };

  // Calculate stats for timeline - ensure displayedReports is an array
  const safeDisplayedReports = Array.isArray(displayedReports) ? displayedReports : [];
  // Only count uncollected reports for the timeline
  const pendingReports = safeDisplayedReports.filter(r => r.collected === 0);
  const redReports = pendingReports.filter(r => getStatusColor(r) === '#F55F5F').length;
  const yellowReports = pendingReports.filter(r => getStatusColor(r) === '#E0E000').length;
  const blueReports = pendingReports.filter(r => getStatusColor(r) === 'lightblue').length;
  const orangeReports = pendingReports.filter(r => getStatusColor(r) === '#FFA726').length; // Upcoming
  const greenReports = pendingReports.filter(r => getStatusColor(r) === 'lightgreen').length;

  // Individual editing functions
  const handleEditIndividual = () => {
    setEditingIndividual(true);
    setEditedIndividual({
      name: individual.name || '',
      case_manager: individual.case_manager || '',
      case_manager_email: individual.case_manager_email || '',
      case_manager_phone_number: individual.case_manager_phone_number || '',
      building_location_id: individual.building_location_id || ''
    });
  };

  const handleSaveIndividual = async () => {
    try {
      await fetchAuth(`/api/individuals/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedIndividual)
      });
      setIndividual({ ...individual, ...editedIndividual });
      setEditingIndividual(false);
      setUpdateStatus('success');
      setTimeout(() => setUpdateStatus(null), 3000);
    } catch (error) {
      console.error('Error updating individual:', error);
      setError('Failed to update individual information');
    }
  };

  // Contact editing functions
  const handleEditContact = (contact) => {
    setEditingContact(contact.id);
    setEditedContact({
      contact_name: contact.contact_name || '',
      relationship: contact.relationship || '',
      email: contact.email || '',
      phone_number: contact.phone_number || ''
    });
  };

  const handleSaveContact = async (contactId) => {
    try {
      await fetchAuth(`/api/contacts/${contactId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedContact)
      });
      setContacts(contacts.map(c => 
        c.id === contactId ? { ...c, ...editedContact } : c
      ));
      setEditingContact(null);
      setUpdateStatus('success');
      setTimeout(() => setUpdateStatus(null), 3000);
    } catch (error) {
      console.error('Error updating contact:', error);
      setError('Failed to update contact information');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await fetchAuth(`/api/contacts/${contactId}`, {
        method: 'DELETE'
      });
      setContacts(contacts.filter(c => c.id !== contactId));
      setUpdateStatus('success');
      setTimeout(() => setUpdateStatus(null), 3000);
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Failed to delete contact');
    }
  };

  const handleAddNewContact = async () => {
    try {
      const response = await fetchAuth(`/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          individual_id: parseInt(id),
          ...newContact
        })
      });
      
      // Refresh contacts list after adding
      const contactsData = await fetchAuth(`/api/individuals/${id}/contacts`);
      setContacts(contactsData);
      
      setAddingNewContact(false);
      setNewContact({
        contact_name: '',
        relationship: '',
        email: '',
        phone_number: ''
      });
      setUpdateStatus('success');
      setTimeout(() => setUpdateStatus(null), 3000);
    } catch (error) {
      console.error('Error adding contact:', error);
      setError('Failed to add new contact');
    }
  };

  // =====================
  // INDIVIDUAL NOTES FUNCTIONS
  // =====================

  const fetchIndividualNotes = async (individualId) => {
    setNotesLoading(true);
    try {
      const notesData = await fetchAuth(`/api/individuals/${individualId}/notes`);
      setIndividualNotes(Array.isArray(notesData) ? notesData : []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setIndividualNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddIndividualNote = async () => {
    if (!newNoteData.content.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/individuals/${id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newNoteData)
      });

      if (response.ok) {
        const createdNote = await response.json();
        setIndividualNotes([createdNote, ...individualNotes]);
        setAddingNote(false);
        setNewNoteData({ title: '', content: '' });
        setUpdateStatus('success');
        setTimeout(() => setUpdateStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error adding note:', error);
      setError('Failed to add note');
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note.id);
    setEditedNoteData({ title: note.title || '', content: note.content });
  };

  const handleSaveNote = async (noteId) => {
    if (!editedNoteData.content.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/individuals/${id}/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedNoteData)
      });

      if (response.ok) {
        const updatedNote = await response.json();
        setIndividualNotes(individualNotes.map(n => n.id === noteId ? updatedNote : n));
        setEditingNote(null);
        setEditedNoteData({ title: '', content: '' });
        setUpdateStatus('success');
        setTimeout(() => setUpdateStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error updating note:', error);
      setError('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const response = await fetchWithAuth(`/api/individuals/${id}/notes/${noteId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setIndividualNotes(individualNotes.filter(n => n.id !== noteId));
        setUpdateStatus('success');
        setTimeout(() => setUpdateStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      setError('Failed to delete note');
    }
  };

  const handleTogglePin = async (noteId) => {
    try {
      const response = await fetchWithAuth(`/api/individuals/${id}/notes/${noteId}/pin`, {
        method: 'PATCH'
      });

      if (response.ok) {
        const { is_pinned } = await response.json();
        setIndividualNotes(
          individualNotes
            .map(n => n.id === noteId ? { ...n, is_pinned } : n)
            .sort((a, b) => {
              if (a.is_pinned !== b.is_pinned) return b.is_pinned - a.is_pinned;
              return new Date(b.created_at) - new Date(a.created_at);
            })
        );
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const formatNoteDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today at ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  // Manage Forms Dialog handlers
  const handleOpenManageForms = async () => {
    setManageFormsLoading(true);
    setManageFormsOpen(true);

    try {
      const response = await fetchWithAuth(`/api/individuals/${id}/forms`);
      if (response.ok) {
        const data = await response.json();
        setAvailableReportTypes(data);
        // Pre-select currently assigned forms
        const assignedIds = data.filter(rt => rt.is_assigned).map(rt => rt.id);
        setSelectedFormIds(assignedIds);
      }
    } catch (error) {
      console.error('Error fetching form assignments:', error);
      setError('Failed to load form assignments');
    } finally {
      setManageFormsLoading(false);
    }
  };

  const handleCloseManageForms = () => {
    setManageFormsOpen(false);
    setSelectedFormIds([]);
    setDrOrdersDateOfIssue('');
  };

  const handleFormToggle = (formId) => {
    setSelectedFormIds(prev => {
      if (prev.includes(formId)) {
        // Clear date of issue if Dr Orders is being unchecked
        if (formId === DR_ORDERS_FORM_ID) {
          setDrOrdersDateOfIssue('');
        }
        return prev.filter(id => id !== formId);
      } else {
        return [...prev, formId];
      }
    });
  };

  const handleSaveFormAssignments = async () => {
    // Check if adding Dr Orders and date of issue is required
    const hadDrOrders = availableReportTypes.find(rt => rt.id === DR_ORDERS_FORM_ID && rt.is_assigned);
    const addingDrOrders = selectedFormIds.includes(DR_ORDERS_FORM_ID) && !hadDrOrders;

    if (addingDrOrders && !drOrdersDateOfIssue) {
      setError('Date of Issue is required for Doctor\'s Orders');
      return;
    }

    setManageFormsLoading(true);

    try {
      const response = await fetchWithAuth(`/api/individuals/${id}/forms`, {
        method: 'PUT',
        body: JSON.stringify({
          form_ids: selectedFormIds,
          dr_orders_date_of_issue: drOrdersDateOfIssue || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        setUpdateStatus('success');
        setTimeout(() => setUpdateStatus(null), 3000);
        handleCloseManageForms();
        // Refresh forms data
        if (currentYear) {
          fetchFormsData(id, currentYear);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update form assignments');
      }
    } catch (error) {
      console.error('Error updating form assignments:', error);
      setError('Failed to update form assignments');
    } finally {
      setManageFormsLoading(false);
    }
  };

  // Handle deactivate/archive individual
  const handleDeactivateIndividual = async () => {
    if (!window.confirm(`Are you sure you want to archive ${individual?.name}? This will deactivate their record but can be reversed later.`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/individuals/${id}/deactivate`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Archived by user' })
      });

      if (response.ok) {
        setUpdateStatus('success');
        setTimeout(() => setUpdateStatus(null), 3000);
        // Navigate back to individuals list
        navigate('/people');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to archive individual');
      }
    } catch (error) {
      console.error('Error archiving individual:', error);
      setError('Failed to archive individual');
    }
  };

  // Generate HUD summary
  const generateHUDSummary = () => {
    if (!individual || !displayedReports.length) return null;
    
    const firstName = individual.name.split(' ')[0];
    const totalForms = displayedReports.length;
    const collectedForms = displayedReports.filter(r => r.collected === 1).length;
    const awaitingForms = totalForms - collectedForms;
    
    // Get most recent submission
    const recentSubmissions = displayedReports
      .filter(r => r.collected === 1 && r.collected_date)
      .sort((a, b) => new Date(b.collected_date) - new Date(a.collected_date));
    const lastSubmission = recentSubmissions[0];
    
    // Get overdue and upcoming forms
    const now = new Date();
    const pendingForms = displayedReports.filter(r => r.collected === 0);
    
    const overdueForms = pendingForms.filter(r => {
      const dueDate = new Date(r.due_date);
      return now > dueDate;
    });
    
    const upcomingForms = pendingForms
      .filter(r => {
        const dueDate = new Date(r.due_date);
        return dueDate >= now;
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    
    const nextForm = upcomingForms[0];
    const daysUntilNext = nextForm ? Math.floor((new Date(nextForm.due_date) - now) / (1000 * 60 * 60 * 24)) : null;
    
    // Create personalized message based on status
    let mainMessage = '';
    let mood = 'neutral';
    
    if (awaitingForms === 0) {
      mainMessage = `🎉 Amazing! ${firstName} is all caught up for ${currentYear}! All ${totalForms} forms have been submitted. What a superstar! 🌟`;
      mood = 'celebration';
    } else if (overdueForms.length > 0) {
      const overdueCount = overdueForms.length;
      mainMessage = `For ${currentYear}, ${firstName} has ${awaitingForms} form${awaitingForms > 1 ? 's' : ''} to complete. `;
      if (lastSubmission) {
        const submitterName = lastSubmission.collected_by_name || 'a team member';
        const dateStr = formatDateDisplay(lastSubmission.collected_date);
        mainMessage += `The last form was submitted by ${submitterName} on ${dateStr}. `;
      }
      mainMessage += `⏰ Heads up - ${overdueCount} form${overdueCount > 1 ? 's are' : ' is'} overdue. Let's tackle ${overdueCount > 1 ? 'those' : 'that'} first!`;
      mood = 'urgent';
    } else if (daysUntilNext !== null && daysUntilNext <= 7) {
      mainMessage = `For ${currentYear}, ${firstName} has ${awaitingForms} form${awaitingForms > 1 ? 's' : ''} remaining. `;
      if (lastSubmission) {
        const submitterName = lastSubmission.collected_by_name || 'a team member';
        const dateStr = formatDateDisplay(lastSubmission.collected_date);
        mainMessage += `Great progress! The last form was submitted by ${submitterName} on ${dateStr}. `;
      }
      if (daysUntilNext === 0) {
        mainMessage += `📋 The next form is due today! Let's keep the momentum going!`;
      } else if (daysUntilNext === 1) {
        mainMessage += `📅 Just 1 day until the next form is due - almost there!`;
      } else {
        mainMessage += `📅 ${daysUntilNext} days until the next form is due. You've got this!`;
      }
      mood = 'encouraging';
    } else {
      mainMessage = `For ${currentYear}, ${firstName} has ${awaitingForms} form${awaitingForms > 1 ? 's' : ''} to complete. `;
      if (lastSubmission) {
        const submitterName = lastSubmission.collected_by_name || 'a team member';
        const dateStr = formatDateDisplay(lastSubmission.collected_date);
        mainMessage += `Nice work! The last form was submitted by ${submitterName} on ${dateStr}. `;
      }
      if (daysUntilNext !== null) {
        mainMessage += `✨ You have ${daysUntilNext} days until the next form is due - plenty of time to prepare!`;
      } else {
        mainMessage += `Keep up the great work! 💪`;
      }
      mood = 'positive';
    }
    
    // Add encouraging sub-messages
    const encouragements = {
      celebration: ['Keep up this incredible work!', 'This is what excellence looks like!', 'Time to celebrate this achievement!'],
      urgent: ['Every form counts!', "Let's turn this around together!", 'One step at a time - you can do this!'],
      encouraging: ['Great momentum!', 'Keep pushing forward!', "You're making progress!"],
      positive: ['Steady progress wins the race!', 'Looking good!', 'Keep it up!']
    };
    
    const randomEncouragement = encouragements[mood][Math.floor(Math.random() * encouragements[mood].length)];
    
    return {
      mainMessage,
      encouragement: randomEncouragement,
      mood,
      stats: {
        total: totalForms,
        collected: collectedForms,
        pending: awaitingForms,
        overdue: overdueForms.length
      }
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hudSummary = useMemo(() => generateHUDSummary(), [individual, displayedReports, currentYear]);

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        INDIVIDUAL REPORTS
      </Typography>
      
      {/* Display error if any */}
      {error && (
        <Box sx={{ 
          p: 2, 
          mb: 2, 
          bgcolor: 'error.light', 
          color: 'error.contrastText',
          borderRadius: 1
        }}>
          <Typography>{error}</Typography>
        </Box>
      )}
      
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="individual-select-label">Choose Individual</InputLabel>
        <Select
          labelId="individual-select-label"
          value={id || ""}
          onChange={handleIndividualSelect}
          label="Choose Individual"
        >
          <MenuItem value="" disabled>Choose Individual</MenuItem>
          {filteredIndividuals.map((individual) => (
            <MenuItem key={individual.id} value={individual.id}>{individual.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Year Navigation */}
      {individual && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <YearNavigation 
            individualId={id}
            currentYear={currentYear}
            onYearChange={setCurrentYear}
          />
        </Box>
      )}

      {isLoading ? (
        <IndividualSkeleton />
      ) : (
        <>
          {/* HUD Summary */}
          {hudSummary && currentYear && (
            <Card sx={{ 
              mb: 4, 
              bgcolor: hudSummary.mood === 'urgent' ? '#fff3cd' : 
                       hudSummary.mood === 'celebration' ? '#d4edda' : 
                       hudSummary.mood === 'encouraging' ? '#cce5ff' : '#e8f4fd',
              borderLeft: `6px solid ${
                hudSummary.mood === 'urgent' ? '#ffc107' : 
                hudSummary.mood === 'celebration' ? '#28a745' : 
                hudSummary.mood === 'encouraging' ? '#0066cc' : '#17a2b8'
              }`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  mb: 2,
                  color: hudSummary.mood === 'urgent' ? '#856404' : 
                         hudSummary.mood === 'celebration' ? '#155724' : 
                         hudSummary.mood === 'encouraging' ? '#004085' : '#0c5460'
                }}>
                  {hudSummary.mainMessage}
                </Typography>
                <Typography variant="body1" sx={{ 
                  fontStyle: 'italic',
                  fontWeight: 500,
                  color: hudSummary.mood === 'urgent' ? '#856404' : 
                         hudSummary.mood === 'celebration' ? '#155724' : 
                         hudSummary.mood === 'encouraging' ? '#004085' : '#0c5460',
                  mb: 2
                }}>
                  {hudSummary.encouragement}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 3 }}>
                  <Chip 
                    icon={<InfoIcon />}
                    label={`${hudSummary.stats.total} Total Forms`}
                    color="default"
                    variant="outlined"
                  />
                  <Chip 
                    label={`${hudSummary.stats.collected} Collected`}
                    color="success"
                    variant={hudSummary.stats.collected > 0 ? "filled" : "outlined"}
                  />
                  <Chip 
                    label={`${hudSummary.stats.pending} Pending`}
                    color={hudSummary.stats.pending > 0 ? "warning" : "default"}
                    variant={hudSummary.stats.pending > 0 ? "filled" : "outlined"}
                  />
                  {hudSummary.stats.overdue > 0 && (
                    <Chip 
                      label={`${hudSummary.stats.overdue} Overdue`}
                      color="error"
                      variant="filled"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Individual Information Card */}
          <Card sx={{ mb: 4, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" color="primary">
                  {individual?.name || "No Individual Selected"}
                </Typography>
                {individual && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AssignmentIcon />}
                      onClick={handleOpenManageForms}
                    >
                      Manage Forms
                    </Button>
                    <DirectRequestButton
                      individualId={individual.id}
                      individualName={individual.name}
                      variant="outlined"
                      size="small"
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      startIcon={<ArchiveIcon />}
                      onClick={handleDeactivateIndividual}
                    >
                      Archive
                    </Button>
                    <IconButton
                      onClick={editingIndividual ? () => setEditingIndividual(false) : handleEditIndividual}
                      color="primary"
                    >
                      {editingIndividual ? <CancelIcon /> : <EditIcon />}
                    </IconButton>
                  </Box>
                )}
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {editingIndividual ? (
                    <>
                      <TextField
                        fullWidth
                        label="Name"
                        value={editedIndividual.name}
                        onChange={(e) => setEditedIndividual({...editedIndividual, name: e.target.value})}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Case Manager"
                        value={editedIndividual.case_manager}
                        onChange={(e) => setEditedIndividual({...editedIndividual, case_manager: e.target.value})}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Email"
                        value={editedIndividual.case_manager_email}
                        onChange={(e) => setEditedIndividual({...editedIndividual, case_manager_email: e.target.value})}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Phone"
                        value={editedIndividual.case_manager_phone_number}
                        onChange={(e) => setEditedIndividual({...editedIndividual, case_manager_phone_number: e.target.value})}
                        sx={{ mb: 2 }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="body1">
                        <strong>Case Manager:</strong> {individual?.case_manager}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Email:</strong> {individual?.case_manager_email}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Phone:</strong> {individual?.case_manager_phone_number}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">
                    <strong>Building:</strong> {individual?.building_location_name}
                  </Typography>
                  {editingIndividual && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveIndividual}
                        sx={{ mr: 1 }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => setEditingIndividual(false)}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Contacts Information */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Contacts
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setAddingNewContact(true)}
                  size="small"
                  variant="outlined"
                >
                  Add Contact
                </Button>
              </Box>
              
              {contacts.length > 0 ? (
                <Grid container spacing={2}>
                  {contacts.map((contact) => (
                    <Grid item xs={12} md={6} key={contact.id}>
                      <Card variant="outlined">
                        <CardContent>
                          {editingContact === contact.id ? (
                            <>
                              <TextField
                                fullWidth
                                size="small"
                                label="Name"
                                value={editedContact.contact_name}
                                onChange={(e) => setEditedContact({...editedContact, contact_name: e.target.value})}
                                sx={{ mb: 1 }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Relationship"
                                value={editedContact.relationship}
                                onChange={(e) => setEditedContact({...editedContact, relationship: e.target.value})}
                                sx={{ mb: 1 }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Email"
                                value={editedContact.email}
                                onChange={(e) => setEditedContact({...editedContact, email: e.target.value})}
                                sx={{ mb: 1 }}
                              />
                              <TextField
                                fullWidth
                                size="small"
                                label="Phone"
                                value={editedContact.phone_number}
                                onChange={(e) => setEditedContact({...editedContact, phone_number: e.target.value})}
                                sx={{ mb: 1 }}
                              />
                              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSaveContact(contact.id)}
                                  startIcon={<SaveIcon />}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setEditingContact(null)}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {contact.contact_name}
                                </Typography>
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditContact(contact)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteContact(contact.id)}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                              <Typography variant="body2">
                                <strong>Relationship:</strong> {contact.relationship || "N/A"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Email:</strong> {contact.email || "N/A"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Phone:</strong> {contact.phone_number || "N/A"}
                              </Typography>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography>No contacts available for this individual.</Typography>
              )}
              
              {/* Add New Contact Dialog */}
              <Dialog open={addingNewContact} onClose={() => setAddingNewContact(false)} maxWidth="sm" fullWidth>
                <DialogContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>Add New Contact</Typography>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    value={newContact.contact_name}
                    onChange={(e) => setNewContact({...newContact, contact_name: e.target.value})}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Relationship"
                    value={newContact.relationship}
                    onChange={(e) => setNewContact({...newContact, relationship: e.target.value})}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={newContact.phone_number}
                    onChange={(e) => setNewContact({...newContact, phone_number: e.target.value})}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setAddingNewContact(false)}>
                      Cancel
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleAddNewContact}
                      disabled={!newContact.contact_name}
                    >
                      Add Contact
                    </Button>
                  </Box>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Individual Notes Section */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ pb: notesExpanded ? 2 : '16px !important' }}>
              {/* Collapsible Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                  borderRadius: 1,
                  mx: -1,
                  px: 1,
                  py: 0.5
                }}
                onClick={() => setNotesExpanded(!notesExpanded)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NoteIcon sx={{ color: '#7c4dff' }} />
                  <Typography variant="h6">Notes</Typography>
                  {individualNotes.length > 0 && (
                    <Chip
                      label={individualNotes.length}
                      size="small"
                      sx={{ bgcolor: '#ede7f6', color: '#7c4dff', fontWeight: 600 }}
                    />
                  )}
                  {notesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNotesExpanded(true);
                    setAddingNote(true);
                  }}
                  size="small"
                  sx={{ bgcolor: '#7c4dff', '&:hover': { bgcolor: '#651fff' } }}
                >
                  Add Note
                </Button>
              </Box>

              {/* Preview when collapsed - show most recent note */}
              {!notesExpanded && individualNotes.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    bgcolor: '#f8f5ff',
                    borderRadius: 1,
                    borderLeft: '3px solid #b39ddb',
                    cursor: 'pointer'
                  }}
                  onClick={() => setNotesExpanded(true)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {!!individualNotes[0].is_pinned && <PushPinIcon sx={{ fontSize: 14, color: '#7c4dff' }} />}
                    <Typography variant="caption" color="text.secondary">
                      {individualNotes[0].author_employee_name || individualNotes[0].author_name} · {formatNoteDate(individualNotes[0].created_at)}
                    </Typography>
                  </Box>
                  {individualNotes[0].title && (
                    <Typography variant="body2" fontWeight="600" sx={{ color: '#5e35b1' }}>
                      {individualNotes[0].title}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {individualNotes[0].content}
                  </Typography>
                  {individualNotes.length > 1 && (
                    <Typography variant="caption" sx={{ color: '#7c4dff', mt: 0.5, display: 'block' }}>
                      +{individualNotes.length - 1} more note{individualNotes.length > 2 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Empty state when collapsed */}
              {!notesExpanded && individualNotes.length === 0 && !addingNote && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, fontStyle: 'italic' }}
                >
                  No notes yet
                </Typography>
              )}

              {/* Expanded Content */}
              <Collapse in={notesExpanded || addingNote}>
                <Box sx={{ mt: 2 }}>
                  {/* Add Note Form */}
                  <Collapse in={addingNote}>
                    <Paper sx={{ p: 2, mb: 2, bgcolor: '#fafafa', border: '1px solid #e8e8e8' }}>
                      <TextField
                        fullWidth
                        label="Title (optional)"
                        value={newNoteData.title}
                        onChange={(e) => setNewNoteData({ ...newNoteData, title: e.target.value })}
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Note content"
                        value={newNoteData.content}
                        onChange={(e) => setNewNoteData({ ...newNoteData, content: e.target.value })}
                        multiline
                        rows={4}
                        placeholder="Write your note here... You can include observations, important information, or anything relevant to this individual."
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          onClick={() => {
                            setAddingNote(false);
                            setNewNoteData({ title: '', content: '' });
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleAddIndividualNote}
                          disabled={!newNoteData.content.trim()}
                          sx={{ bgcolor: '#7c4dff', '&:hover': { bgcolor: '#651fff' } }}
                        >
                          Save Note
                        </Button>
                      </Box>
                    </Paper>
                  </Collapse>

                  {/* Notes List */}
                  {notesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} sx={{ color: '#7c4dff' }} />
                    </Box>
                  ) : individualNotes.length === 0 && !addingNote ? (
                    <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f5ff', border: '1px dashed #b39ddb' }}>
                      <NoteIcon sx={{ fontSize: 48, color: '#b39ddb', mb: 1 }} />
                      <Typography color="text.secondary">
                        No notes yet. Add a note to keep track of important information about this individual.
                      </Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={2}>
                      {individualNotes.map((note) => (
                        <Paper
                          key={note.id}
                          sx={{
                            p: 2,
                            borderLeft: note.is_pinned ? '4px solid #7c4dff' : '4px solid transparent',
                            bgcolor: note.is_pinned ? '#ede7f6' : 'white',
                            transition: 'all 0.2s'
                          }}
                        >
                          {editingNote === note.id ? (
                            // Edit mode
                            <>
                              <TextField
                                fullWidth
                                label="Title (optional)"
                                value={editedNoteData.title}
                                onChange={(e) => setEditedNoteData({ ...editedNoteData, title: e.target.value })}
                                size="small"
                                sx={{ mb: 2 }}
                              />
                              <TextField
                                fullWidth
                                label="Note content"
                                value={editedNoteData.content}
                                onChange={(e) => setEditedNoteData({ ...editedNoteData, content: e.target.value })}
                                multiline
                                rows={4}
                                sx={{ mb: 2 }}
                              />
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button
                                  size="small"
                                  onClick={() => setEditingNote(null)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSaveNote(note.id)}
                                  disabled={!editedNoteData.content.trim()}
                                  sx={{ bgcolor: '#7c4dff', '&:hover': { bgcolor: '#651fff' } }}
                                >
                                  Save
                                </Button>
                              </Box>
                            </>
                          ) : (
                            // View mode
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                <Box sx={{ flex: 1 }}>
                                  {note.title && (
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                      {!!note.is_pinned && <PushPinIcon sx={{ fontSize: 16, mr: 0.5, color: '#7c4dff' }} />}
                                      {note.title}
                                    </Typography>
                                  )}
                                  {!note.title && !!note.is_pinned && (
                                    <PushPinIcon sx={{ fontSize: 16, color: '#7c4dff', mb: 1 }} />
                                  )}
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: 'pre-wrap',
                                      color: 'text.primary',
                                      lineHeight: 1.6
                                    }}
                                  >
                                    {note.content}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', ml: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleTogglePin(note.id)}
                                    sx={{ color: note.is_pinned ? '#7c4dff' : 'inherit' }}
                                    title={note.is_pinned ? 'Unpin note' : 'Pin note'}
                                  >
                                    <PushPinIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditNote(note)}
                                    title="Edit note"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteNote(note.id)}
                                    color="error"
                                    title="Delete note"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                              <Divider sx={{ my: 1.5 }} />
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {note.author_employee_name || note.author_name}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {formatNoteDate(note.created_at)}
                                  {note.updated_at !== note.created_at && ' (edited)'}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* Timeline visualization (similar to dashboard) */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Report Status Timeline</Typography>

              <Grid container spacing={1} alignItems="center" justifyContent="space-between">
                <Grid item sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: 'lightgreen',
                    mx: 'auto',
                    mb: 0.5
                  }} />
                  <Typography variant="caption">Current</Typography>
                </Grid>
                <Grid item sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: '#FFA726',
                    mx: 'auto',
                    mb: 0.5
                  }} />
                  <Typography variant="caption">Due Soon</Typography>
                </Grid>
                <Grid item sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: 'lightblue',
                    mx: 'auto',
                    mb: 0.5
                  }} />
                  <Typography variant="caption">1-45 days</Typography>
                </Grid>
                <Grid item sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: '#E0E000',
                    mx: 'auto',
                    mb: 0.5
                  }} />
                  <Typography variant="caption">46-90 days</Typography>
                </Grid>
                <Grid item sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    bgcolor: '#F55F5F',
                    mx: 'auto',
                    mb: 0.5
                  }} />
                  <Typography variant="caption">90+ days</Typography>
                </Grid>
              </Grid>

              <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                  height: 8,
                  borderRadius: 2,
                  mt: 1,
                  mb: 2,
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(to right, lightgreen 20%, #FFA726 20%, #FFA726 40%, lightblue 40%, lightblue 60%, #E0E000 60%, #E0E000 80%, #F55F5F 80%)'
                  }
                }}
              />
              
              {/* Stats cards */}
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={2.4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Current</Typography>
                          <Typography variant="h6">{greenReports}</Typography>
                        </Box>
                        <Box sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: 'lightgreen',
                        }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={2.4}>
                  <Card variant="outlined" sx={{ borderColor: orangeReports > 0 ? '#FFA726' : undefined }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">Due Soon</Typography>
                          <Typography variant="h6" sx={{ color: orangeReports > 0 ? '#FFA726' : 'inherit' }}>{orangeReports}</Typography>
                        </Box>
                        <Box sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: '#FFA726',
                        }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={2.4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">1-45 days</Typography>
                          <Typography variant="h6">{blueReports}</Typography>
                        </Box>
                        <Box sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: 'lightblue',
                        }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={2.4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">46-90 days</Typography>
                          <Typography variant="h6">{yellowReports}</Typography>
                        </Box>
                        <Box sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: '#E0E000',
                        }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={2.4}>
                  <Card variant="outlined" sx={{ borderColor: redReports > 0 ? '#F55F5F' : undefined }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary">90+ days</Typography>
                          <Typography variant="h6" sx={{ color: redReports > 0 ? '#F55F5F' : 'inherit' }}>{redReports}</Typography>
                        </Box>
                        <Box sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: '#F55F5F',
                        }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {/* Filter and Search Controls */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3, alignItems: { sm: 'center' } }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              aria-label="Report Filter"
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="collected">Collected</ToggleButton>
              <ToggleButton value="awaiting">Awaiting</ToggleButton>
            </ToggleButtonGroup>
            
            <Button
              variant={showLogHistory ? "contained" : "outlined"}
              onClick={() => setShowLogHistory(!showLogHistory)}
              size="small"
              sx={{ ml: 'auto' }}
            >
              {showLogHistory ? 'Hide Log History' : 'Show Log History'}
            </Button>
            
            <TextField
              label="Search reports"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              fullWidth
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
              }}
            />
          </Box>
          
          {/* Floating Update Button with Status */}
          <Box
            sx={{
              position: 'fixed',
              bottom: '20px',
              right: '20px',
              zIndex: 1000,
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)',
              borderRadius: '50px',
              padding: '10px 20px',
              backgroundColor: 'white',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            {updateStatus === 'success' && (
              <Chip 
                label="Successfully updated" 
                color="success" 
                size="small"
                sx={{ fontSize: '0.8rem' }}
              />
            )}
            
            {updateStatus === 'error' && (
              <Chip 
                label="Update failed" 
                color="error" 
                size="small"
                sx={{ fontSize: '0.8rem' }}
              />
            )}
            
            <Button 
              variant="contained" 
              color="primary" 
              disabled={Object.keys(updatedReports).length === 0 || updateStatus === 'loading'} 
              onClick={handleUpdateAll}
              sx={{ 
                borderRadius: '50px',
                px: 3,
                minWidth: '120px'
              }}
            >
              {updateStatus === 'loading' ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Updating...
                </Box>
              ) : (
                `Update All (${Object.keys(updatedReports).length})`
              )}
            </Button>
          </Box>

          {/* Conditional Rendering: Log History or Reports Table */}
          {showLogHistory ? (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Form Log History</Typography>
              {displayedReports.map((form) => (
                <FormLogHistory 
                  key={form.id}
                  form={form}
                  individual={individual}
                />
              ))}
            </Box>
          ) : (
            /* Reports Display - Mobile Cards or Desktop Table */
            isMobile ? (
              /* Mobile Card View */
              <Stack spacing={2} sx={{ mb: 4 }}>
                {!Array.isArray(displayedReports) || displayedReports.length === 0 ? (
                  <Card>
                    <CardContent>
                      <Typography align="center">No reports found</Typography>
                    </CardContent>
                  </Card>
                ) : (
                  displayedReports.map((report) => (
                    <Card key={report.id} sx={{ position: 'relative' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6">
                              {report.report_name}
                            </Typography>
                            <Chip
                              label={getStatusChip(report).label}
                              color={getStatusChip(report).color}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                          <Box sx={{ 
                            width: 24, 
                            height: 24, 
                            backgroundColor: getStatusColor(report),
                            borderRadius: '50%',
                            position: 'relative'
                          }}>
                            {updatedReports[report.id] && (
                              <Box sx={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                width: 8,
                                height: 8,
                                backgroundColor: 'orange',
                                borderRadius: '50%',
                              }} />
                            )}
                          </Box>
                        </Box>
                        
                        {/* Form fields - show differently based on collected status */}
                        {report.collected && !updatedReports[report.id] ? (
                          /* Read-only view for collected forms */
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Document Date
                                </Typography>
                                <Typography variant="body2" sx={{ minHeight: '24px' }}>
                                  {formatDateDisplay(report.due_date) || 'No document date'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Due Date
                                </Typography>
                                <Typography variant="body2" sx={{ minHeight: '24px' }}>
                                  {formatDateDisplay(report.collected_date) || 'No date recorded'}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Status
                                </Typography>
                                <Typography variant="body2" color="success.main" fontWeight="bold">
                                  ✓ Form Collected {report.collected_date && calculateDaysBetween(report.due_date, report.collected_date) !== null &&
                                    `(${calculateDaysBetween(report.due_date, report.collected_date)} days ${
                                      new Date(report.collected_date) > new Date(report.due_date) ? 'after' : 'before'
                                    } document date)`
                                  }
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        ) : (
                          /* Editable view for pending forms */
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Document Date
                                </Typography>
                                <DatePicker
                                  selected={
                                    updatedReports[report.id]?.due_date !== undefined
                                      ? (updatedReports[report.id].due_date ? parseDate(updatedReports[report.id].due_date) : null)
                                      : (report.due_date ? parseDate(report.due_date) : null)
                                  }
                                  onChange={(date) => handleDateChange(date, report.id, "due_date")}
                                  dateFormat="MM-dd-yyyy"
                                  customInput={<TextField size="small" fullWidth />}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Due Date
                                </Typography>
                                <DatePicker
                                  selected={
                                    updatedReports[report.id]?.collected_date !== undefined
                                      ? (updatedReports[report.id].collected_date ? parseDate(updatedReports[report.id].collected_date) : null)
                                      : (report.collected_date ? parseDate(report.collected_date) : null)
                                  }
                                  onChange={(date) => handleDateChange(date, report.id, "collected_date")}
                                  dateFormat="MM-dd-yyyy"
                                  customInput={<TextField size="small" fullWidth />}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  Collected Status
                                </Typography>
                                <RadioGroup
                                  row
                                  value={updatedReports[report.id]?.collected !== undefined ? updatedReports[report.id].collected : report.collected}
                                  onChange={(e) => handleCollectedChange(report.id, parseInt(e.target.value, 10))}
                                  sx={{ ml: 0 }}
                                >
                                  <FormControlLabel 
                                    value={1} 
                                    control={<Radio size="small" />} 
                                    label="Yes"
                                    sx={{ mr: 3 }}
                                  />
                                  <FormControlLabel 
                                    value={0} 
                                    control={<Radio size="small" />} 
                                    label="No" 
                                  />
                                </RadioGroup>
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </Stack>
            ) : (
              /* Desktop Table View */
              <TableContainer component={Paper} sx={{ mb: 4, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', overflowX: 'auto' }}>
              <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '10%' }}>Status</TableCell>
                  <TableCell sx={{ width: '25%' }}>Report</TableCell>
                  <TableCell sx={{ width: '15%' }}>Document Date</TableCell>
                  <TableCell sx={{ width: '15%' }}>Due Date</TableCell>
                  <TableCell sx={{ width: '20%' }}>Days Between</TableCell>
                  <TableCell sx={{ width: '15%' }}>Collected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!Array.isArray(displayedReports) || displayedReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No reports found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: getStatusColor(report),
                            borderRadius: '50%',
                            position: 'relative',
                          }}
                        >
                          {updatedReports[report.id] && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -4,
                                right: -4,
                                width: 8,
                                height: 8,
                                backgroundColor: 'orange',
                                borderRadius: '50%',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {report.report_name}
                        </Typography>
                        <Chip
                          label={getStatusChip(report).label}
                          color={getStatusChip(report).color}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </TableCell>
                      <TableCell>
                        {report.collected && !updatedReports[report.id] ? (
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {formatDateDisplay(report.due_date) || 'No document date'}
                          </Typography>
                        ) : (
                          <DatePicker
                            selected={
                              updatedReports[report.id]?.due_date !== undefined
                                ? (updatedReports[report.id].due_date ? parseDate(updatedReports[report.id].due_date) : null)
                                : (report.due_date ? parseDate(report.due_date) : null)
                            }
                            onChange={(date) => handleDateChange(date, report.id, "due_date")}
                            dateFormat="MM-dd-yyyy"
                            customInput={<TextField size="small" fullWidth />}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {report.collected && !updatedReports[report.id] ? (
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {formatDateDisplay(report.collected_date) || 'No date recorded'}
                          </Typography>
                        ) : (
                          <DatePicker
                            selected={
                              updatedReports[report.id]?.collected_date !== undefined
                                ? (updatedReports[report.id].collected_date ? parseDate(updatedReports[report.id].collected_date) : null)
                                : (report.collected_date ? parseDate(report.collected_date) : null)
                            }
                            onChange={(date) => handleDateChange(date, report.id, "collected_date")}
                            dateFormat="MM-dd-yyyy"
                            customInput={<TextField size="small" fullWidth />}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {report.collected && report.collected_date ? (
                          <Typography variant="body2" sx={{ fontWeight: 'medium', color: new Date(report.collected_date) > new Date(report.due_date) ? 'error.main' : 'success.main', fontSize: '0.875rem' }}>
                            {calculateDaysBetween(report.due_date, report.collected_date)} days {
                              new Date(report.collected_date) > new Date(report.due_date) ? 'late' : 'early'
                            }
                          </Typography>
                        ) : report.collected_date ? (
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {calculateDaysBetween(report.due_date, report.collected_date)} days
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Only disable if form is truly saved as collected AND no pending changes */}
                        {report.collected && !updatedReports[report.id] ? (
                          <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                            ✓ Form Collected
                          </Typography>
                        ) : (
                          <RadioGroup
                            row
                            value={updatedReports[report.id]?.collected !== undefined ? updatedReports[report.id].collected : report.collected}
                            onChange={(e) =>
                              handleCollectedChange(report.id, parseInt(e.target.value, 10))
                            }
                          >
                            <FormControlLabel 
                              value={1} 
                              control={<Radio size="small" />} 
                              label="Yes" 
                            />
                            <FormControlLabel 
                              value={0} 
                              control={<Radio size="small" />} 
                              label="No" 
                            />
                          </RadioGroup>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </TableContainer>
            )
          )}
        </>
      )}

      {/* Notes Modal */}
      <Dialog open={notesModalVisible} onClose={() => setNotesModalVisible(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography variant="h6">Form Notes</Typography>
          <Box sx={{ mb: 2 }}>
            {selectedNotes.length > 0 ? selectedNotes.map((note, index) => (
              <Typography key={index}>{note}</Typography>
            )) : <Typography>No notes available</Typography>}
          </Box>
          <TextField
            label="Add a new note"
            fullWidth
            multiline
            rows={3}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleAddNote}
              disabled={!newNote.trim()}
            >
              Add Note
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Manage Forms Dialog */}
      <Dialog
        open={manageFormsOpen}
        onClose={handleCloseManageForms}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" />
            Manage Form Assignments
          </Box>
          <Typography variant="body2" color="text.secondary">
            Select which forms this individual needs to complete for {currentYear}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {manageFormsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormGroup>
                <Grid container spacing={1}>
                  {availableReportTypes.map((rt) => (
                    <Grid item xs={12} sm={6} key={rt.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFormIds.includes(rt.id)}
                            onChange={() => handleFormToggle(rt.id)}
                            disabled={rt.collected === 1}
                          />
                        }
                        label={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: selectedFormIds.includes(rt.id) ? 600 : 400,
                                color: rt.collected ? 'success.main' : 'inherit'
                              }}
                            >
                              {rt.report_name}
                              {rt.collected === 1 && (
                                <Chip
                                  label="Collected"
                                  size="small"
                                  color="success"
                                  sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Typography>
                            {rt.description && (
                              <Typography variant="caption" color="text.secondary">
                                {rt.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>

              {/* Date of Issue for Dr Orders */}
              {selectedFormIds.includes(DR_ORDERS_FORM_ID) && !availableReportTypes.find(rt => rt.id === DR_ORDERS_FORM_ID && rt.is_assigned) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Doctor's Orders - Date of Issue Required
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Enter the date the Doctor's Orders were issued. The form will be due for renewal 180 days from this date.
                  </Typography>
                  <TextField
                    type="date"
                    label="Date of Issue"
                    value={drOrdersDateOfIssue}
                    onChange={(e) => setDrOrdersDateOfIssue(e.target.value)}
                    size="small"
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200, bgcolor: 'white' }}
                  />
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Note:</strong> Collected forms cannot be removed. Only uncollected forms can be added or removed.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManageForms}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveFormAssignments}
            disabled={manageFormsLoading}
            startIcon={manageFormsLoading ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default People;
